import { useContext, useState } from 'react';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';

import { RowExpandedModalCombinedProps } from './types';
import { deleteEntryUseCase } from './RowExpandedModal.interactor';
import { TableStoreContext } from '../../../stores/TableStoreContext';
import { getModalRows } from './utils';
import { getTranslation } from '../../../lib/reactUtils';
import { useParams, useLocation } from 'react-router';
import { DatabasePageParams } from '../../pages/DatabasePage/types';
import { getQueryParams } from '../../../lib/utils';

const usePresenter = (props: RowExpandedModalCombinedProps): RowExpandedModalCombinedProps => {
  const { t } = useTranslation();
  const tableStore = useContext(TableStoreContext);
  const { databaseSlug, tableSlug } = useParams<DatabasePageParams>();
  const { search } = useLocation();
  const queryParams = getQueryParams(search);
  const page = queryParams.page ? Number(queryParams.page) : 1;
  const perPage = queryParams.perPage ? Number(queryParams.perPage) : 100;
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const { mutateAsync: deleteEntry } = useMutation('deleteEntry', async (id?: string) => {
    if (id && tableSlug) {
      await deleteEntryUseCase(databaseSlug, tableSlug, id);
      tableStore.removeSelectedEntry();
    }
  });

  const handleDeleteButtonClick = () => {
    setShowDeleteModal(true);
  };

  const performDelete = async () => {
    if (tableStore.selectedRow !== undefined) {
      await deleteEntry(tableStore.tableState[tableStore.selectedRow].entry.uuid);
    }
  };

  const closeModal = () => {
    tableStore.setSelectedRow(undefined);
    tableStore.toggleModal();
  };

  const hideDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmButtonClick = async () => {
    await performDelete();
    hideDeleteModal();
    tableStore.toggleModal();
  };

  const getRowNumber = () => {
    const indexOnPage = (tableStore.selectedRow || 0) + 1;
    const currentRowNumber  = (page - 1) * perPage + indexOnPage;
    return currentRowNumber;
  };

  return {
    ...props,
    tableName: {
      value: tableStore.table?.name,
    },
    rowNumber: {
      value: t('database_page.modal_texts.row', {
        rowNumber: getRowNumber(),
      }),
    },
    button: {
      icon: {
        asset: 'Delete',
      },
      onClick: handleDeleteButtonClick,
    },
    closeButton: {
      icon: {
        asset: 'Close',
      },
      onClick: closeModal,
    },
    modalRowList: {
      modalRowItems: getModalRows(tableStore.table),
    },
    deleteModal: {
      modalProps: {
        type: 'Default',
        styleType: 'Default',
        title: {
          value: t('database_page.modal_texts.delete_modal.title'),
        },
        button: {
          onClick: hideDeleteModal,
        },
        text: {
          value: getTranslation('database_page.modal_texts.delete_modal.text', {
            rowNumber: getRowNumber(),
            tableName: tableStore.table?.name,
          }),
        },
        actionButton: {
          text: {
            value: t('database_page.modal_texts.delete_modal.action_button'),
          },
          onClick: handleConfirmButtonClick,
        },
        cancelButton: {
          text: {
            value: t('database_page.modal_texts.delete_modal.cancel_button'),
          },
          onClick: hideDeleteModal,
        },
      },
      show: showDeleteModal,
    },
  };
};

export default usePresenter;
